import React, { useEffect, useMemo, useState } from 'react';
import { Favorite, Visibility } from '@mui/icons-material';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CLASSES } from '../core/characterUtils';
import { normTagIds } from '../core/criteriaTags';
import { useNotification, NOTIFICATION } from '../components/NotificationSystem';
import BuildListHeader from '../components/build/BuildListHeader';
import { CompactBuildCard } from '../components/build/CompactBuildCard';
import CommunityBreadcrumb from '../components/community/CommunityBreadcrumb';
import PageTemplate from '../components/layout/PageTemplate';
import { NAV_COMMUNITY, NAV_CREATORS, navLabel } from '../utils/communityNavStrings';
import { listCommunityBuilds, toggleCommunityBuildLike } from '../services/buildApi';
import { formatCount } from '../utils/formatCount';
import './Community.css';
import './MyBuilds.css';

const TXT_LOG_IN_TO_LIKE = { fr: 'Connecte-toi pour liker.', en: 'Log in to like.', de: 'Melde dich an, um zu liken.', es: 'Inicia sesión para dar like.', pt: 'Entre para curtir.' };
const TXT_LIKE_FAILED = { fr: 'Echec du like.', en: 'Like failed.', de: 'Like fehlgeschlagen.', es: 'No se ha podido dar like.', pt: 'Não foi possível curtir.' };
const TXT_CREATOR_STATS = { fr: 'Statistiques du créateur', en: 'Creator stats', de: 'Ersteller-Statistiken', es: 'Estadísticas del creador', pt: 'Estatísticas do criador' };

const TXT = {
    notFound: { fr: 'Créateur introuvable.', en: 'Creator not found.', de: 'Ersteller nicht gefunden.', es: 'Creador no encontrado.', pt: 'Criador não encontrado.' },
    back: { fr: 'Retour à la communauté', en: 'Back to community', de: 'Zurück zur Community', es: 'Volver a la comunidad', pt: 'Voltar à comunidade' },
    statLikes: { fr: 'likes', en: 'likes', de: 'Likes', es: 'likes', pt: 'curtidas' },
    statViews: { fr: 'vues', en: 'views', de: 'Aufrufe', es: 'visitas', pt: 'visualizações' },
    loading: { fr: 'Chargement...', en: 'Loading...', de: 'Wird geladen...', es: 'Cargando...', pt: 'Carregando...' },
    empty: { fr: 'Aucun stuff public pour ce créateur.', en: 'No public builds for this creator.', de: 'Keine öffentlichen Builds für diesen Ersteller.', es: 'Ninguna build pública de este creador.', pt: 'Nenhuma build pública deste criador.' },
    buildsHeading: { fr: 'Stuffs créés', en: 'Created builds', de: 'Erstellte Builds', es: 'Builds creadas', pt: 'Builds criadas' },
    results: { fr: 'résultats', en: 'results', de: 'Ergebnisse', es: 'resultados', pt: 'resultados' },
    search: { fr: 'Rechercher un stuff...', en: 'Search a build...', de: 'Build suchen...', es: 'Buscar una build...', pt: 'Buscar uma build...' },
    sortLabel: { fr: 'Trier par', en: 'Sort by', de: 'Sortieren nach', es: 'Ordenar por', pt: 'Ordenar por' },
    sortLikes: { fr: 'populaires', en: 'popular', de: 'beliebt', es: 'populares', pt: 'populares' },
    sortViews: { fr: 'vues', en: 'views', de: 'Aufrufe', es: 'visitas', pt: 'visualizações' },
    sortRecent: { fr: 'récents', en: 'recent', de: 'neueste', es: 'recientes', pt: 'recentes' },
    prev: { fr: 'Précédent', en: 'Previous', de: 'Zurück', es: 'Anterior', pt: 'Anterior' },
    next: { fr: 'Suivant', en: 'Next', de: 'Weiter', es: 'Siguiente', pt: 'Seguinte' },
};

function t(dict, lang) {
    return dict[lang] || dict.en;
}

function parseCsv(raw) {
    if (!raw) return [];
    return String(raw).split(',').map(x => x.trim()).filter(Boolean);
}

function toCsv(values) {
    return values.length ? values.join(',') : '';
}

const CommunityCreatorBuilds = ({ lang = 'fr' }) => {
    const { publicSlug } = useParams();
    const { isAuthenticated } = useAuth();
    const { showNotification } = useNotification();
    const [params, setParams] = useSearchParams();

    const [rows, setRows] = useState([]);
    const [allTagIds, setAllTagIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [ownerProfile, setOwnerProfile] = useState(undefined);

    const q = params.get('q') || '';
    const sort = params.get('sort') || 'recent';
    const page = Math.max(Number(params.get('page') || 1), 1);
    const selectedClasses = useMemo(() => new Set(parseCsv(params.get('classes'))), [params]);
    const selectedTags = useMemo(() => new Set(parseCsv(params.get('tags'))), [params]);

    const [searchInput, setSearchInput] = useState(q);
    useEffect(() => setSearchInput(q), [q]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput === q) return;
            const next = new URLSearchParams(params);
            if (searchInput.trim()) next.set('q', searchInput.trim());
            else next.delete('q');
            next.set('page', '1');
            setParams(next, { replace: true });
        }, 250);
        return () => clearTimeout(timer);
    }, [searchInput, q, params, setParams]);

    useEffect(() => {
        if (!publicSlug) return;
        let cancelled = false;
        setLoading(true);
        setOwnerProfile(undefined);
        setRows([]);
        setTotal(0);
        const owner = publicSlug || '';
        listCommunityBuilds({
            owner,
            q,
            classes: toCsv(Array.from(selectedClasses)),
            tags: toCsv(Array.from(selectedTags)),
            sort,
            page,
            page_size: 24,
        }).then((data) => {
            if (cancelled) return;
            const results = Array.isArray(data?.results) ? data.results : [];
            setRows(results);
            setTotal(Number(data?.total || 0));
            if (Object.prototype.hasOwnProperty.call(data || {}, 'owner_profile')) {
                setOwnerProfile(data.owner_profile);
            } else {
                setOwnerProfile(null);
            }
            const tags = new Set();
            results.forEach((r) => normTagIds(r?.build?.criteriaTags).forEach(x => tags.add(x)));
            setAllTagIds(Array.from(tags));
        }).catch(() => {
            if (cancelled) return;
            setRows([]);
            setTotal(0);
            setOwnerProfile(null);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [publicSlug, q, selectedClasses, selectedTags, sort, page]);

    const setCsvParam = (name, values) => {
        const next = new URLSearchParams(params);
        const serialized = toCsv(values);
        if (serialized) next.set(name, serialized);
        else next.delete(name);
        next.set('page', '1');
        setParams(next);
    };

    const toggleClass = (name) => {
        const next = new Set(selectedClasses);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        setCsvParam('classes', Array.from(next));
    };

    const toggleTag = (tagId) => {
        const next = new Set(selectedTags);
        if (next.has(tagId)) next.delete(tagId);
        else next.add(tagId);
        setCsvParam('tags', Array.from(next));
    };

    const handleSort = (nextSort) => {
        const next = new URLSearchParams(params);
        next.set('sort', nextSort);
        next.set('page', '1');
        setParams(next);
    };

    const handleLike = async (id, hadLiked) => {
        if (!isAuthenticated) {
            showNotification(TXT_LOG_IN_TO_LIKE[lang] || TXT_LOG_IN_TO_LIKE.en, NOTIFICATION.INFO);
            return;
        }
        setRows(prev => prev.map(r => r.id === id ? {
            ...r,
            viewer: { ...(r.viewer || {}), hasLiked: !hadLiked },
            metrics: { ...(r.metrics || {}), likes: Math.max(0, Number(r?.metrics?.likes || 0) + (hadLiked ? -1 : 1)) },
        } : r));
        try {
            const res = await toggleCommunityBuildLike(id);
            setRows(prev => prev.map(r => r.id === id ? {
                ...r,
                viewer: { ...(r.viewer || {}), hasLiked: !!res.liked },
                metrics: { ...(r.metrics || {}), likes: Number(res.likes || 0) },
            } : r));
        } catch {
            setRows(prev => prev.map(r => r.id === id ? {
                ...r,
                viewer: { ...(r.viewer || {}), hasLiked: hadLiked },
                metrics: { ...(r.metrics || {}), likes: Math.max(0, Number(r?.metrics?.likes || 0) + (hadLiked ? 1 : -1)) },
            } : r));
            showNotification(TXT_LIKE_FAILED[lang] || TXT_LIKE_FAILED.en, NOTIFICATION.ERROR);
        }
    };

    const displayName = ownerProfile?.display_name || ownerProfile?.public_slug || publicSlug || '';
    const notFound = !loading && ownerProfile === null;
    const profileLikes = formatCount(ownerProfile?.total_likes ?? ownerProfile?.metrics?.likes, lang);
    const profileViews = formatCount(ownerProfile?.total_views ?? ownerProfile?.metrics?.views, lang);

    const breadcrumbCrumbs = useMemo(() => {
        const base = [
            { label: navLabel(NAV_COMMUNITY, lang), to: `/${lang}/community` },
            { label: navLabel(NAV_CREATORS, lang), to: `/${lang}/community/creators` },
        ];
        if (notFound) {
            return [...base, { label: publicSlug || '…' }];
        }
        return [...base, { label: displayName || publicSlug || '…' }];
    }, [lang, displayName, publicSlug, notFound]);

    if (!publicSlug) {
        return <Navigate to={`/${lang}/community`} replace />;
    }

    const creatorPageHeader = (
        <header className="community-creator-header">
            {ownerProfile?.avatar_url ? (
                <img className="community-creator-avatar" src={ownerProfile.avatar_url} alt="" />
            ) : null}
            <div>
                <div className="community-creator-stats" aria-label={TXT_CREATOR_STATS[lang] || TXT_CREATOR_STATS.en}>
                    <div className="community-creator-stat">
                        <span className="community-creator-stat-value">
                            <Favorite fontSize="inherit" aria-hidden />
                            <span>{profileLikes}</span>
                        </span>
                        <span className="community-creator-stat-label">{t(TXT.statLikes, lang)}</span>
                    </div>
                    <div className="community-creator-stat">
                        <span className="community-creator-stat-value">
                            <Visibility fontSize="inherit" aria-hidden />
                            <span>{profileViews}</span>
                        </span>
                        <span className="community-creator-stat-label">{t(TXT.statViews, lang)}</span>
                    </div>
                </div>
            </div>
        </header>
    );

    return (
        <PageTemplate
            header={<CommunityBreadcrumb crumbs={breadcrumbCrumbs} />}
        >
            <div className="community-container community-creator-page mybuilds-container">
            {!notFound ? creatorPageHeader : null}

            {notFound ? (
                <div className="mybuilds-empty">
                    <p>{t(TXT.notFound, lang)}</p>
                    <Link className="btn-plain" to={`/${lang}/community`}>{t(TXT.back, lang)}</Link>
                </div>
            ) : (
                <>
                    <BuildListHeader
                        lang={lang}
                        title={<h2 className="community-builds-title">{t(TXT.buildsHeading, lang)}</h2>}
                        classes={CLASSES}
                        selectedClasses={selectedClasses}
                        onToggleClass={toggleClass}
                        tagIds={allTagIds}
                        selectedTags={selectedTags}
                        onToggleTag={toggleTag}
                        searchValue={searchInput}
                        onSearchChange={setSearchInput}
                        searchPlaceholder={t(TXT.search, lang)}
                        leading={<p className="build-list-count">{total} {t(TXT.results, lang)}</p>}
                        sortValue={sort}
                        onSortChange={handleSort}
                        sortLabel={t(TXT.sortLabel, lang)}
                        sortOptions={[
                            { value: 'likes', label: t(TXT.sortLikes, lang) },
                            { value: 'views', label: t(TXT.sortViews, lang) },
                            { value: 'recent', label: t(TXT.sortRecent, lang) },
                        ]}
                    />

                    {loading ? (
                        <div className="mybuilds-empty"><p>{t(TXT.loading, lang)}</p></div>
                    ) : rows.length === 0 ? (
                        <div className="mybuilds-empty"><p>{t(TXT.empty, lang)}</p></div>
                    ) : (
                        <div className="community-builds-grid">
                            {rows.map((entry) => (
                                <div key={entry.id} className="community-builds-cell">
                                    <CompactBuildCard
                                        entry={entry}
                                        lang={lang}
                                        onToggleLike={handleLike}
                                        hideOwner
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {total > 24 && (
                        <div className="community-pagination row">
                            <button className="btn-plain" disabled={page <= 1} onClick={() => setParams(new URLSearchParams({ ...Object.fromEntries(params), page: String(page - 1) }))}>{t(TXT.prev, lang)}</button>
                            <button className="btn-plain" disabled={page * 24 >= total} onClick={() => setParams(new URLSearchParams({ ...Object.fromEntries(params), page: String(page + 1) }))}>{t(TXT.next, lang)}</button>
                        </div>
                    )}
                </>
            )}
        </div>
        </PageTemplate>
    );
};

export default CommunityCreatorBuilds;
